import { useEffect, useState } from 'react';

import { useImsApiContext } from '@/auth/ImsApiContext.tsx';
import {
	getCachedImsAuthId,
	getProxyAuthHeaders,
} from '@/auth/imsTokenStorage';
import { RedditIcon } from '@/components/icons/RedditIcon.tsx';
import { config } from '@/config/env.ts';
import { logger } from '@/logger';
import { formatRelativeTime } from '@/utils/formatRelativeTime.ts';

import type { RedditPostPreview } from '../../../../../bridge/types/messages.ts';

import { subredditFromUrl } from './RedditEmbed.helpers.ts';

type State = 'loading' | 'ready' | 'error';

type RedditPost = {
	subreddit: string;
	title: string;
	author: string;
	selftext: string;
	score: number;
	num_comments: number;
	created_utc: number;
};

// The card draws from one shape whatever the source. A post that came with the
// message carries only the four fields the monitor stored, so every other field
// is optional and the card leaves out the parts it cannot fill.
type CardPost = {
	subreddit: string | null;
	title: string;
	score: number;
	num_comments: number;
	author?: string;
	selftext?: string;
	created_utc?: number;
};

// The chat re-creates the markdown `p` override on every render, so this card
// unmounts and remounts and asks for the same post again. One broken post URL
// produced hundreds of proxy requests in a single page view.
//
// Two module-level maps bound that traffic. `inFlight` holds the request that
// is already running for a URL, so a burst of remounts costs one request
// instead of one each. `fetchAttempts` counts only the requests that finished
// and failed, so a remount never spends the budget and a healthy card always
// reaches 'ready'. Both maps live only for the life of the page, and a
// successful load clears the budget for that URL.
const MAX_FETCH_ATTEMPTS = 3;
const fetchAttempts = new Map<string, number>();
const inFlight = new Map<string, Promise<RedditPost>>();
// A post that has loaded once is kept, so a remount paints from memory instead
// of asking again. Without this the attempt cap bounded only the failure path:
// a success cleared the URL's budget and nothing held the result, so the
// unbounded remount loop returned as soon as previews started working.
const loaded = new Map<string, RedditPost>();

// A request runs to its end even after the component that started it goes
// away. The result decides whether the URL keeps its budget, so an unmount
// must not cancel it.
function fetchPost(url: string, imsToken: string | null): Promise<RedditPost> {
	const cached = loaded.get(url);
	if (cached) return Promise.resolve(cached);

	const running = inFlight.get(url);
	if (running) return running;

	const request = (async () => {
		let status: number | null = null;
		try {
			const resp = await fetch(
				`${config.UI_PROXY_URL}/reddit-post?url=${encodeURIComponent(url)}`,
				{
					headers: {
						...getProxyAuthHeaders(imsToken),
						// Names the customer on the proxy's log line, so one failure
						// can be traced back to the session that saw it. The proxy
						// treats this as a search key and never as an access
						// decision, the same posture the Splunk route keeps.
						'x-user-id': getCachedImsAuthId() ?? '',
					},
				},
			);
			status = resp.status;
			if (!resp.ok) throw new Error(`reddit_post_failed_${resp.status}`);
			const post = (await resp.json()) as RedditPost;
			// A URL that reads all the way through keeps no budget, so a card
			// that loads once can always load again later.
			fetchAttempts.delete(url);
			loaded.set(url, post);
			return post;
		} catch (err) {
			fetchAttempts.set(url, (fetchAttempts.get(url) ?? 0) + 1);
			logger.error('Reddit preview failed', { url, status });
			throw err;
		} finally {
			inFlight.delete(url);
		}
	})();

	inFlight.set(url, request);
	return request;
}

export const RedditEmbed = ({
	url,
	fallbackText,
	storedPost,
}: {
	url: string;
	fallbackText: string;
	storedPost?: RedditPostPreview;
}) => {
	const [state, setState] = useState<State>('loading');
	const [post, setPost] = useState<RedditPost | null>(null);
	const imsContext = useImsApiContext();
	const imsToken = imsContext?.imsToken ?? null;
	const authLoading = imsContext?.loading ?? true;

	useEffect(() => {
		// The message brought its own copy of the post, so there is nothing to
		// ask for. This check comes first, ahead of the auth wait and the
		// attempt budget, or a stored card could still end in the error state.
		if (storedPost) return;
		// Wait for a real IMS token instead of reading a possibly-not-yet-set
		// value: firing early would 403 and burn one of the few permanent
		// retry attempts on a bootstrap race rather than a real failure.
		if (authLoading || !imsToken) return;
		// The budget returns before any request runs, so nothing else can ever
		// reach the error state once it is spent. The card remounts on every
		// chat render, so without this the customer would see the failure once
		// and then a bare link for the rest of the session.
		if ((fetchAttempts.get(url) ?? 0) >= MAX_FETCH_ATTEMPTS) {
			setState('error');
			return;
		}

		let cancelled = false;

		fetchPost(url, imsToken).then(
			(json) => {
				if (cancelled) return;
				setPost(json);
				setState('ready');
			},
			() => {
				if (cancelled) return;
				setState('error');
			},
		);

		return () => {
			cancelled = true;
		};
	}, [url, imsToken, authLoading, storedPost]);

	// A stored post wins over anything the fetch produced, so the card that has
	// every field it needs is never painted over by a failure.
	const cardPost: CardPost | null = storedPost
		? {
				subreddit: subredditFromUrl(storedPost.url),
				title: storedPost.title,
				score: storedPost.score,
				num_comments: storedPost.num_comments,
			}
		: state === 'ready' && post
			? {
					subreddit: post.subreddit || subredditFromUrl(url),
					title: post.title,
					score: post.score,
					num_comments: post.num_comments,
					author: post.author,
					selftext: post.selftext,
					created_utc: post.created_utc,
				}
			: null;

	if (!cardPost) {
		// A failure used to look the same as a link that had not loaded yet, so a
		// customer waited for a card that was never coming. Name the failure.
		if (state === 'error') {
			return (
				<div
					data-testid="reddit-embed-error"
					className="my-3 flex items-center gap-2 rounded-xl border
						border-gray-200 bg-white px-4 py-3 text-sm text-gray-600"
				>
					<RedditIcon className="h-5 w-5 shrink-0 text-gray-900" />
					<span>Reddit preview unavailable</span>
					<a
						href={url}
						target="_blank"
						rel="noopener noreferrer"
						className="underline decoration-gray-500"
					>
						{fallbackText}
					</a>
				</div>
			);
		}
		return (
			<a
				href={url}
				target="_blank"
				rel="noopener noreferrer"
				className="underline decoration-gray-500"
			>
				{fallbackText}
			</a>
		);
	}

	// A stored post carries no timestamp. Building the date first would throw a
	// RangeError before formatRelativeTime could guard it.
	const postedAt =
		typeof cardPost.created_utc === 'number' &&
		Number.isFinite(cardPost.created_utc)
			? formatRelativeTime(new Date(cardPost.created_utc * 1000).toISOString())
			: null;

	// A stored post has no author either. Drop the whole row when neither part
	// survives, so the card shows no empty line and no stray separator.
	const metaParts = [
		cardPost.author ? `Posted by u/${cardPost.author}` : null,
		postedAt,
	].filter(Boolean);

	return (
		<div
			data-testid="reddit-embed-card"
			className="my-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
		>
			<div className="flex items-center gap-3">
				<RedditIcon className="h-8 w-8 shrink-0 text-gray-900" />
				<div className="min-w-0 flex-1">
					{cardPost.subreddit && (
						<div className="text-sm font-semibold text-gray-900">
							r/{cardPost.subreddit}
						</div>
					)}
					{metaParts.length > 0 && (
						<div className="text-xs text-gray-500">{metaParts.join(' · ')}</div>
					)}
				</div>
			</div>

			<div className="my-3 border-t border-gray-200" />

			<div className="text-base font-bold text-gray-900">{cardPost.title}</div>

			{cardPost.selftext?.trim() && (
				<div
					className="mt-2 line-clamp-4 text-sm whitespace-pre-wrap
						text-gray-700"
				>
					{cardPost.selftext}
				</div>
			)}

			<div className="mt-3 flex items-center gap-4 text-sm text-gray-600">
				<div className="flex items-center gap-1">
					<UpArrowIcon />
					<span>{cardPost.score}</span>
					<DownArrowIcon />
				</div>
				<div className="flex items-center gap-1">
					<CommentIcon />
					<span>{cardPost.num_comments} comments</span>
				</div>
				<a
					href={url}
					target="_blank"
					rel="noopener noreferrer"
					className="ml-auto flex items-center gap-1 font-medium text-[#FF4500]"
				>
					View on Reddit
					<ExternalLinkIcon />
				</a>
			</div>
		</div>
	);
};

const UpArrowIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M8 12V4M4 8l4-4 4 4" />
	</svg>
);

const DownArrowIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M8 4v8M4 8l4 4 4-4" />
	</svg>
);

const CommentIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M14 9a2 2 0 0 1-2 2H6l-3 3V4a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v5z" />
	</svg>
);

const ExternalLinkIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M10 2h4v4M14 2L7.5 8.5M12 9v4a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h4" />
	</svg>
);
